import API_ENDPOINTS from "@/common/api/apiEndpoints";
import httpClient from "@/common/api/httpClient";
import httpMethods from "@/common/api/httpMethods";
import type {
  TestCases,
  TestSteps,
  TestCaseType,
  TestCasePriority,
  TestCaseStatusEntity,
  TestData,
  TestRequirement,
  NaturalTextActions,
  Elements,
  ElementScreenName,
  DefectLog,
  TestCaseResults,
  Comment,
  History,
  TestCaseFolder,
  Tag,
  ManualTestRun,
  ForLoopCondition,
  AgentDevices,
  Paginated,
} from "@/common/types/generated";
import { buildQueryString } from "@/common/utils/utils";
import { TableConfig } from "@/config/appConfig";

interface QueryParams {
  versionId: number;
  testCaseFolderId?: number;
  page?: number;
  size?: number;
  query?: string;
  sort?: string;
  deleted?: boolean;
  tags?: string;
  isStepGroup?: boolean;
  includeStepGroups?: boolean;
}

const buildQueryParams = (params: QueryParams): string => {
  const {
    versionId,
    testCaseFolderId,
    page = 0,
    size = TableConfig.ITEMS_PER_PAGE,
    query = "",
    sort = "name,asc",
    deleted,
    tags,
    isStepGroup,
    includeStepGroups,
  } = params;

  const searchParams = new URLSearchParams();

  searchParams.set("versionId", String(versionId));
  searchParams.set("page", String(page));
  searchParams.set("size", String(size));
  searchParams.set("query", query);
  searchParams.set("sort", sort);

  if (deleted !== undefined) {
    searchParams.set("deleted", String(deleted));
  }

  // ✅ Only set testCaseFolderId if it's defined
  if (testCaseFolderId !== undefined && testCaseFolderId !== null) {
    searchParams.set("testCaseFolderId", String(testCaseFolderId));
  }

  if (tags) {
    searchParams.set("tags", tags);
  }

  if (isStepGroup !== undefined) {
    searchParams.set("isStepGroup", String(isStepGroup));
  }

  if (includeStepGroups !== undefined) {
    searchParams.set("includeStepGroups", String(includeStepGroups));
  }

  return `?${searchParams.toString()}`;
};

type GenericQueryParams = {
  query?: string;
  sort?: string;
  page?: number;
  size?: number;
  [key: string]: string | number | boolean | null | undefined;
};

export const buildSpecialQueryParams = (params: GenericQueryParams): string => {
  const { query, ...rest } = params;

  const searchParams = new URLSearchParams();

  Object.entries(rest).forEach(([key, value]) => {
    if (value !== null && value !== undefined) {
      searchParams.set(key, String(value));
    }
  });

  // Manually prepend `query` if present
  const queryPart = query ? `query=${query}` : "";

  return `?${[queryPart, searchParams.toString()].filter(Boolean).join("&")}`;
};

export const fetchManualTestCase = (params: QueryParams) => {
  const endpoint = `${API_ENDPOINTS.TESTCASE.manualTest}${buildQueryParams(params)}`;
  return httpClient<Paginated<TestCases>>(httpMethods.GET, endpoint);
};

export const fetchAutomatedTestCase = (params: QueryParams) => {
  const endpoint = `${API_ENDPOINTS.TESTCASE.automatedTest}${buildQueryParams(params)}`;
  return httpClient<Paginated<TestCases>>(httpMethods.GET, endpoint);
};

export const fetchStepGroups = (params: QueryParams) => {
  const {
    versionId,
    page = 0,
    size = TableConfig.ITEMS_PER_PAGE,
    sort = "name,asc",
  } = params;

  const query = `workspaceVersionId:${versionId},isStepGroup:true,deleted:false`;
  const endpoint = `${API_ENDPOINTS.TESTCASE.testCase}?query=${query}&sort=${sort}&page=${page}&size=${size}`;
  return httpClient<Paginated<TestCases>>(httpMethods.GET, endpoint);
};

export const fetchPerformanceTestCase = (params: QueryParams) => {
  const endpoint = `${API_ENDPOINTS.TESTCASE.performanceTest}${buildQueryParams(params)}`;
  return httpClient<Paginated<TestCases>>(httpMethods.GET, endpoint);
};

export const fetchAllTestCases = (params: QueryParams) => {
  const endpoint = `${API_ENDPOINTS.TESTCASE.allTestCases}${buildQueryParams(params)}&_=${Date.now()}`;
  return httpClient<Paginated<TestCases>>(httpMethods.GET, endpoint);
};

export interface SimilarityCandidatePayload {
  id: string | number;
  title?: string;
  steps?: { step?: string; expected?: string }[];
}

export interface SimilarityMatch {
  testCaseId: number;
  name: string;
  folderName: string | null;
  score: number;
  level: "duplicate" | "similar";
}

export interface SimilarityResult {
  candidateId: string;
  match: SimilarityMatch | null;
}

/**
 * Ask the backend which AI-generated candidates look like near-duplicates of
 * existing test cases in the workspace version (title + steps comparison).
 */
export const checkSimilarTestCases = (payload: {
  workspaceVersionId: number | string;
  candidates: SimilarityCandidatePayload[];
  /** Scope detection to the same type ("manual" | "automation"). Omit for legacy
   *  type-agnostic matching. */
  mode?: "manual" | "automation";
}) => {
  const endpoint = `${API_ENDPOINTS.TESTCASE.testCase}similarity_check`;
  return httpClient<{ data: SimilarityResult[] }>(
    httpMethods.POST,
    endpoint,
    payload,
  );
};

export interface TestCaseRunHistory {
  recentRuns: {
    result: "pass" | "fail" | string;
    overallStatus?: string | null;
    durationMs: number | null;
    at: string | null;
    cycleName?: string | null;
    cycleId?: number | null;
    origin?: "automation" | "manual";
    caseResultId?: number | null;
    cycleRunId?: number | null;
    cycleCaseId?: number | null;
  }[];
  health: {
    lastRun: string;
    passRate: number;
    avgTimeMs: number | null;
  } | null;
}

export const fetchTestCaseRunHistory = (testCaseId: number | string) => {
  const endpoint = API_ENDPOINTS.TESTCASE.testCaseRunHistory(testCaseId);
  return httpClient<TestCaseRunHistory>(httpMethods.GET, endpoint);
};

export interface TestCaseCycleRun {
  origin: "automation" | "manual";
  runNumber: number | null;
  cycleId: number | null;
  cycleName: string | null;
  /** Configuration the result ran under — set for automation runs that execute
   *  across multiple browsers/OSes, so same-run rows are distinguishable. */
  configName?: string | null;
  configLabel?: string | null;
  browser?: string | null;
  os?: string | null;
  resolution?: string | null;
  result: string | null;
  durationMs: number | null;
  startTime: string | null;
  endTime: string | null;
  createdDate: string | null;
  caseResultId: number | null;
  cycleRunId: number | null;
  executionId: number | null;
  cycleCaseId: number | null;
}

export interface TestCaseCycleRunsPage {
  content: TestCaseCycleRun[];
  totalElements: number;
  totalPages: number;
  number: number;
  size: number;
}

export const fetchTestCaseCycleRuns = (
  testCaseId: number | string,
  params?: { size?: number; page?: number; type?: "automation" | "manual" },
) => {
  const base = API_ENDPOINTS.TESTCASE.testCaseCycleRuns(testCaseId);
  const qs = new URLSearchParams();
  if (params?.size != null) qs.set("size", String(params.size));
  if (params?.page != null) qs.set("page", String(params.page));
  if (params?.type) qs.set("type", params.type);
  const endpoint = qs.toString() ? `${base}?${qs.toString()}` : base;
  return httpClient<TestCaseCycleRunsPage>(httpMethods.GET, endpoint);
};

// ─── Linked requirements (M:N) ───────────────────────────────────

export interface LinkedRequirement {
  id: number;
  name: string | null;
  requirementType: string | null;
  status: string | null;
  priority: string | null;
  description: string | null;
}

export const fetchTestCaseRequirements = (testCaseId: number | string) => {
  const endpoint = API_ENDPOINTS.TESTCASE.testCaseRequirements(testCaseId);
  return httpClient<{ data: LinkedRequirement[] }>(
    httpMethods.GET,
    endpoint,
  ).then((res) => res.data.data ?? []);
};

export const linkTestCaseRequirements = (
  testCaseId: number | string,
  requirementIds: number[],
) => {
  const endpoint = API_ENDPOINTS.TESTCASE.testCaseRequirements(testCaseId);
  return httpClient<{ data: LinkedRequirement[] }>(httpMethods.POST, endpoint, {
    requirement_ids: requirementIds,
  }).then((res) => res.data.data ?? []);
};

export const unlinkTestCaseRequirement = (
  testCaseId: number | string,
  requirementId: number | string,
) => {
  const endpoint = API_ENDPOINTS.TESTCASE.testCaseRequirementLink(
    testCaseId,
    requirementId,
  );
  return httpClient<{ data: LinkedRequirement[] }>(
    httpMethods.DELETE,
    endpoint,
  ).then((res) => res.data.data ?? []);
};

export const fetchTestCaseTypes = (projectId: number) => {
  const endpoint = `${API_ENDPOINTS.TESTCASE.testCaseTypes}?query=projectId:${projectId}`;
  return httpClient<Paginated<TestCaseType>>(httpMethods.GET, endpoint);
};

export const fetchTestCaseTypeById = (testCaseTypeId: number) => {
  const endpoint = `${API_ENDPOINTS.TESTCASE.testCaseTypes}${testCaseTypeId}`;
  return httpClient<TestCaseType>(httpMethods.GET, endpoint);
};

export const fetchTestCasePriorities = (projectId: number) => {
  const endpoint = `${API_ENDPOINTS.TESTCASE.testCasePriorities}?query=projectId:${projectId}`;
  return httpClient<Paginated<TestCasePriority>>(httpMethods.GET, endpoint);
};

// Priorities are now a single global configuration (Critical/High/Medium/Low),
// no longer scoped per project.
export const fetchPriorities = () => {
  const endpoint = `${API_ENDPOINTS.TESTCASE.priorities}`;
  return httpClient<Paginated<TestCasePriority>>(httpMethods.GET, endpoint);
};

export const fetchTestCasePriorityById = (priorityId: number) => {
  const endpoint = `${API_ENDPOINTS.TESTCASE.testCasePriorities}${priorityId}`;
  return httpClient<TestCasePriority>(httpMethods.GET, endpoint);
};

export const fetchTestCaseStatuses = (projectId: number) => {
  const endpoint = `${API_ENDPOINTS.TESTCASE.testCaseStatuses}?query=projectId:${projectId}`;
  return httpClient<Paginated<TestCaseStatusEntity>>(httpMethods.GET, endpoint);
};

export const fetchTestCaseStatusById = (statusId: number) => {
  const endpoint = `${API_ENDPOINTS.TESTCASE.testCaseStatuses}${statusId}`;
  return httpClient<TestCaseStatusEntity>(httpMethods.GET, endpoint);
};

export const fetchTestData = (testDataId: number) => {
  const endpoint = `${API_ENDPOINTS.TESTCASE.testData}?query=id:${testDataId}`;
  return httpClient<Paginated<TestData>>(httpMethods.GET, endpoint);
};

export const fetchTestDataById = (testDataId: number) => {
  const endpoint = `${API_ENDPOINTS.TESTDATA.testData}/${testDataId}`;
  return httpClient<TestData>(httpMethods.GET, endpoint);
};
export const updateTestCasetypesById = (
  typeId: number,
  payload: Partial<TestCaseType>,
) => {
  const endpoint = `${API_ENDPOINTS.TESTCASE.testCaseTypes}${typeId}`;
  return httpClient<void>(httpMethods.PUT, endpoint, payload);
};
export const fetchRequirements = (versionId: number) => {
  const endpoint = `${API_ENDPOINTS.TESTCASE.testRequirement}?query=workspaceVersionId:${versionId},deleted:false`;
  return httpClient<Paginated<TestRequirement>>(httpMethods.GET, endpoint);
};

export const fetchTeamMembers = () => {
  const params = new URLSearchParams({ page: "0", size: "500" });
  const endpoint = `${API_ENDPOINTS.USER.testMemberList}?${params.toString()}`;
  return httpClient(httpMethods.GET, endpoint);
};

export const fetchDevelopers = () => {
  return httpClient(httpMethods.GET, "user/developers");
};

export const fetchReadyTestCases = (versionId: number) => {
  const params = new URLSearchParams({
    workspaceVersionId: String(versionId),
    deleted: "false",
    size: "200",
  });
  const endpoint = `${API_ENDPOINTS.TESTCASE.testCase}?${params.toString()}`;
  return httpClient<Paginated<TestCases>>(httpMethods.GET, endpoint);
};

export const fetchTestTags = () => {
  const endpoint = `${API_ENDPOINTS.TESTCASE.testCaseTags}`;
  return httpClient<Paginated<Tag>>(httpMethods.GET, endpoint);
};

export const fetchTestCaseTagsById = (testCaseId: string | number) => {
  const endpoint = `${API_ENDPOINTS.TESTCASE.testCaseTags}associate_item/${testCaseId}`;
  return httpClient<Tag[]>(httpMethods.GET, endpoint);
};

export const fetchDefectTagsById = (defectId: string | number) => {
  const endpoint = `${API_ENDPOINTS.DEFECTLOG.defectTags}associate_item/${defectId}`;
  return httpClient<Tag[]>(httpMethods.GET, endpoint);
};

export const associateTestCaseTags = (
  testCaseId: string | number,
  tagNames: string[],
) => {
  const endpoint = `${API_ENDPOINTS.TESTCASE.testCaseTags}associate_item/${testCaseId}`;
  return httpClient<Tag[]>(httpMethods.POST, endpoint, tagNames);
};

export const associateDefectTags = (
  defectId: string | number,
  tagNames: string[],
) => {
  const endpoint = `${API_ENDPOINTS.DEFECTLOG.defectTags}associate_item/${defectId}`;
  return httpClient<Tag[]>(httpMethods.POST, endpoint, tagNames);
};

export const validateURLAccess = (url: string) => {
  const endpoint = `${API_ENDPOINTS.TESTCASE.validateURL}?currentUrl=${url}`;
  return httpClient(httpMethods.GET, endpoint);
};

export const checkTestCaseNameExists = (
  name: string,
  workspaceVersionId: number,
  excludeId?: number,
) => {
  const params = new URLSearchParams({
    name: encodeURIComponent(name),
    workspaceVersionId: String(workspaceVersionId),
  });
  if (excludeId != null) params.set("excludeId", String(excludeId));
  const endpoint = `${API_ENDPOINTS.TESTCASE.checkNameExists}?name=${encodeURIComponent(name)}&workspaceVersionId=${workspaceVersionId}${excludeId != null ? `&excludeId=${excludeId}` : ""}`;
  return httpClient(httpMethods.GET, endpoint);
};

export const saveTestCase = (payload: Partial<TestCases>) => {
  const endpoint = `${API_ENDPOINTS.TESTCASE.testCase}`;
  return httpClient<TestCases>(httpMethods.POST, endpoint, payload);
};

export const copyTestCaseAsStepGroup = (payload: {
  name: string;
  testCaseId: number;
  stepIds: number[];
  isStepGroup: boolean;
  isReplace: boolean;
}) => {
  const endpoint = `${API_ENDPOINTS.TESTCASE.testCase}copy`;
  return httpClient<void>(httpMethods.POST, endpoint, payload);
};

export const updateTestCase = (
  testCaseId: number,
  payload: Partial<TestCases>,
) => {
  const endpoint = `${API_ENDPOINTS.TESTCASE.testCase}${testCaseId}`;
  return httpClient<void>(httpMethods.PUT, endpoint, payload);
};

// Narrow endpoint: only reassigns the Reviewer, so the currently-assigned
// Reviewer can use it even when they aren't the test case's creator (see
// requireCanEditReviewerField on the backend).
export const updateTestCaseReviewer = (
  testCaseId: number,
  reviewerid: number | null,
) => {
  const endpoint = `${API_ENDPOINTS.TESTCASE.testCase}${testCaseId}/reviewer`;
  return httpClient<void>(httpMethods.PUT, endpoint, { reviewerid });
};

// Test Page API's

export const fetchTestCaseById = (testCaseId: string | number) => {
  const endpoint = `${API_ENDPOINTS.TESTCASE.testCase}${testCaseId}`;
  return httpClient<TestCases>(httpMethods.GET, endpoint);
};

export const fetchTestCaseActivity = (
  versionId: string,
  testCaseId: string,
) => {
  const endpoint = `${API_ENDPOINTS.HISTORY.history}?query=workspaceVersionId:${versionId},moduleId:${testCaseId},moduleName:TestCase`;
  return httpClient<Paginated<History>>(httpMethods.GET, endpoint);
};

export const fetchExtensionStatus = () => {
  const endpoint = `${API_ENDPOINTS.TESTCASE.extensionStatus}?query=workspaceType:WebApplication,status!UNINSTALLED`;
  return httpClient(httpMethods.GET, endpoint);
};

export const fetchNaturalTextActions = (params?: {
  search?: string;
  page?: number;
  size?: number;
  workspaceType?: string;
  applicationType?: string;
}) => {
  const appType =
    params?.applicationType || params?.workspaceType || "WebApplication";
  let endpoint = `${API_ENDPOINTS.RECORDER.naturalTextActions}?query=deprecated:false,applicationType:${appType}`;
  if (params?.page !== undefined) endpoint += `&page=${params.page}`;
  if (params?.size !== undefined) endpoint += `&size=${params.size}`;
  if (params?.search)
    endpoint += `&search=${encodeURIComponent(params.search)}`;
  return httpClient<Paginated<NaturalTextActions>>(httpMethods.GET, endpoint);
};

export const fetchNaturalTextActionExamples = (naturalTextActionId: string) => {
  const endpoint = `${API_ENDPOINTS.TESTCASE.naturalTextActions}/${naturalTextActionId}/example`;
  return httpClient(httpMethods.GET, endpoint);
};

export const fetchTestStepDetails = (
  testCaseId: string,
  position: string,
  testStepId: string,
) => {
  const endpoint = `${API_ENDPOINTS.TESTCASE.testSteps}/${testCaseId}/${position}/${testStepId}`;
  return httpClient<TestSteps>(httpMethods.GET, endpoint);
};

export const fetchTestSteps = (testCaseId: string | number) => {
  const endpoint = `${API_ENDPOINTS.RECORDER.testSteps}?query=testCaseId:${testCaseId}&sort=position`;
  return httpClient<Paginated<TestSteps>>(httpMethods.GET, endpoint);
};

export const fetchStepGroupSteps = (stepGroupId: string | number) => {
  const endpoint = `${API_ENDPOINTS.RECORDER.testSteps}?query=testCaseId:${stepGroupId}&sort=position`;
  return httpClient<Paginated<TestSteps>>(httpMethods.GET, endpoint);
};

export const fetchForLoopConditions = () => {
  const endpoint = `${API_ENDPOINTS.TESTCASE.forLoopConditions}`;
  return httpClient<Paginated<ForLoopCondition>>(httpMethods.GET, endpoint);
};

export const fetchAllTestData = (versionId: number) => {
  const endpoint = `${API_ENDPOINTS.TESTCASE.testData}?query=versionId:${versionId}`;
  return httpClient<Paginated<TestData>>(httpMethods.GET, endpoint);
};

export const fetchDryRunHistory = (params: GenericQueryParams) => {
  const endpoint = `${API_ENDPOINTS.TESTCASE.testCaseResults}${buildSpecialQueryParams(params)}`;
  return httpClient<Paginated<TestCaseResults>>(httpMethods.GET, endpoint);
};

// Dry Run

// Fetch Platform Screen Resolution by ID and lab type
export const fetchPlatformScreenResolution = (
  platformScreenResolutionId: string,
  testPlanLabType: string,
) => {
  const endpoint = `${API_ENDPOINTS.PLATFORMS.osVersion}/${platformScreenResolutionId}/screen_resolution?testPlanLabType=${encodeURIComponent(testPlanLabType)}`;
  return httpClient(httpMethods.GET, endpoint);
};

// Fetch Platform OS Version by ID and lab type
export const fetchPlatformOsVersion = (
  platformOsVersionId: string,
  testPlanLabType: string,
) => {
  const endpoint = `${API_ENDPOINTS.PLATFORMS.osVersion}/${platformOsVersionId}/os_version?testPlanLabType=${encodeURIComponent(testPlanLabType)}`;
  return httpClient(httpMethods.GET, endpoint);
};

// Fetch Platform Browsers list by platform ID, OS version, workspaceType and lab type
export const fetchPlatformBrowsers = (
  platformId: string,
  osVersion: string | undefined,
  workspaceType: string,
  testPlanLabType: string,
) => {
  const endpoint = `${API_ENDPOINTS.PLATFORMS.osVersion}/${platformId}/${encodeURIComponent(osVersion || "")}/browsers?workspaceType=${encodeURIComponent(workspaceType)}&testPlanLabType=${encodeURIComponent(testPlanLabType)}`;
  return httpClient(httpMethods.GET, endpoint);
};

// Fetch Cloud Device by platform device ID and lab type
export const fetchCloudDevice = (
  platformDeviceId: string,
  testPlanLabType: string,
) => {
  const endpoint = `${API_ENDPOINTS.PLATFORMS.osVersion}/${platformDeviceId}/device?testPlanLabType=${encodeURIComponent(testPlanLabType)}`;
  return httpClient(httpMethods.GET, endpoint);
};

export const fetchAgentDevices = (
  agentId: string,
  pageNumber: number,
  provisioning: boolean | string,
) => {
  const endpoint = `${API_ENDPOINTS.AGENT.agentDevices}?agentId=${encodeURIComponent(agentId)}&page=${pageNumber}&provisioned=${provisioning}`;
  return httpClient<Paginated<AgentDevices>>(httpMethods.GET, endpoint);
};

// Defect Log
export const fetchDefectLogs = (
  testcaseId: string,
  page: number,
  versionId: number,
) => {
  const endpoint = `${API_ENDPOINTS.DEFECTLOG.defectLog}?query=deleted:false,testCaseId:${testcaseId},versionId:${versionId}&sort=title,asc&page=${page}&size=${TableConfig.ITEMS_PER_PAGE}`;
  return httpClient<Paginated<DefectLog>>(httpMethods.GET, endpoint);
};

export const fetchAllDefectLogs = (testcaseId: number) => {
  const endpoint = `${API_ENDPOINTS.DEFECTLOG.defectLog}?query=deleted:false,testCaseId:${testcaseId}&sort=title,asc`;
  return httpClient<Paginated<DefectLog>>(httpMethods.GET, endpoint);
};

// Elements
export const fetchAllElementList = (
  versionId: string,
  page: number,
  size?: number,
) => {
  const endpoint = `${API_ENDPOINTS.ELEMENTS.elementFilter}1?versionId=${versionId}&sort=createdDate,desc&page=${page}&size=${size || TableConfig.ITEMS_PER_PAGE}`;
  return httpClient<Paginated<Elements>>(httpMethods.GET, endpoint);
};

export const fetchUnusedElementList = (versionId: string, page: number) => {
  const endpoint = `${API_ENDPOINTS.ELEMENTS.elementFilter}6?versionId=${versionId}&sort=createdDate,desc&page=${page}&size=${TableConfig.ITEMS_PER_PAGE}`;
  return httpClient<Paginated<Elements>>(httpMethods.GET, endpoint);
};

export const fetchUsedElementList = (versionId: string, page: number) => {
  const endpoint = `${API_ENDPOINTS.ELEMENTS.elementFilter}7?versionId=${versionId}&sort=createdDate,desc&page=${page}&size=${TableConfig.ITEMS_PER_PAGE}`;
  return httpClient<Paginated<Elements>>(httpMethods.GET, endpoint);
};

export const fetchDuplicateElementList = (versionId: string, page: number) => {
  const endpoint = `${API_ENDPOINTS.ELEMENTS.elementFilter}8?versionId=${versionId}&sort=createdDate,desc&page=${page}&size=${TableConfig.ITEMS_PER_PAGE}`;
  return httpClient<Paginated<Elements>>(httpMethods.GET, endpoint);
};

export const fetchElementScreenName = (versionId: string) => {
  // Fetch up to 500 screens (backend max) in one request so the full list is
  // always visible in the left panel sidebar regardless of how many screens
  // exist. The default page size is 20, which caused newly-created screens
  // beyond the first page to never appear after creation (issue #2212).
  const endpoint = `${API_ENDPOINTS.ELEMENTS.elementScreenName}?query=workspaceVersionId:${versionId}&size=500`;
  return httpClient<Paginated<ElementScreenName>>(httpMethods.GET, endpoint);
};

export const createElementScreenName = (payload) => {
  const endpoint = `${API_ENDPOINTS.ELEMENTS.elementScreenName}`;
  return httpClient<ElementScreenName>(httpMethods.POST, endpoint, payload);
};

export const updateElementScreenName = (id: number | string, payload) => {
  const endpoint = `${API_ENDPOINTS.ELEMENTS.elementScreenName}/${id}`;
  return httpClient<ElementScreenName>(httpMethods.PUT, endpoint, payload);
};

export const deleteElementScreenName = (id: number | string) => {
  const endpoint = `${API_ENDPOINTS.ELEMENTS.elementScreenName}/${id}`;
  return httpClient<void>(httpMethods.DELETE, endpoint);
};

export const creatElement = (payload: Partial<Elements>) => {
  const endpoint = `${API_ENDPOINTS.ELEMENTS.elements}`;
  return httpClient<Elements>(httpMethods.POST, endpoint, payload);
};

export const updateElement = (
  elementId: string,
  payload: Partial<Elements>,
) => {
  const endpoint = `${API_ENDPOINTS.ELEMENTS.elements}/${elementId}`;
  return httpClient<Elements>(httpMethods.PUT, endpoint, payload);
};

export const promoteElementLocator = (
  elementId: number | string,
  payload: { locatorType: string; locatorValue: string },
) => {
  const endpoint = API_ENDPOINTS.ELEMENTS.promotePrimaryLocator(elementId);
  return httpClient<Elements>(httpMethods.PATCH, endpoint, payload);
};

export const fetchElementById = (elementId: number | string) => {
  const endpoint = `${API_ENDPOINTS.ELEMENTS.elements}/${elementId}`;
  return httpClient<Elements>(httpMethods.GET, endpoint);
};

export const deleteElementById = (elementId: number | string) => {
  const endpoint = `${API_ENDPOINTS.ELEMENTS.elements}/${elementId}`;
  return httpClient<void>(httpMethods.DELETE, endpoint);
};

export const fetchElementByName = (versionId: string, elementName: string) => {
  const query = encodeURIComponent(`applicationVersionId:${versionId}`);
  const name = encodeURIComponent(elementName);
  const endpoint = `${API_ENDPOINTS.RECORDER.uiIdentifier}?query=${query}&uiIdentifierName=${name}`;
  return httpClient<Paginated<Elements>>(httpMethods.GET, endpoint);
};

export const fetchElementLinkedEntities = (elementId: number | string) => {
  const endpoint = API_ENDPOINTS.ELEMENTS.linkedEntities(elementId);
  return httpClient(httpMethods.GET, endpoint);
};

export const fetchTestCaseLinkedEntities = (testCaseId: number | string) => {
  const endpoint = API_ENDPOINTS.TESTCASE.testCaseLinkedEntities(testCaseId);
  return httpClient(httpMethods.GET, endpoint);
};
//
export const createFolder = (payload: Partial<TestCaseFolder>) => {
  const endpoint = `${API_ENDPOINTS.TESTCASE.testCaseFolder}`;
  return httpClient<TestCaseFolder>(httpMethods.POST, endpoint, payload);
};

export const createSubFolder = (payload: Partial<TestCaseFolder>) => {
  const endpoint = `${API_ENDPOINTS.TESTCASE.subFolder}`;
  return httpClient<TestCaseFolder>(httpMethods.POST, endpoint, payload);
};

export const createBulkFolder = (payload: Partial<TestCaseFolder>[]) => {
  const endpoint = `${API_ENDPOINTS.TESTCASE.createBulkFolder}`;
  // This endpoint performs many writes against a high-latency remote DB
  // (folders + cases + steps + tags). The global 30s timeout can abort large
  // batches mid-save (leaving rows partially created and the UI stuck on
  // "Saving…"), so allow a longer ceiling for this specific call.
  return httpClient<void>(httpMethods.POST, endpoint, payload, {
    timeout: 120_000,
  });
};

export const updateFolder = (
  folderId: number,
  payload: Partial<TestCaseFolder>,
) => {
  const endpoint = `${API_ENDPOINTS.TESTCASE.testCaseFolder}/${folderId}`;
  return httpClient<void>(httpMethods.PUT, endpoint, payload);
};

export const deleteFolder = (folderId: number | string) => {
  const endpoint = `${API_ENDPOINTS.TESTCASE.testCaseFolder}/${folderId}`;
  return httpClient<void>(httpMethods.DELETE, endpoint);
};

export interface MoveExplorerItemsPayload {
  destinationFolderId: number | null;
  testCaseIds?: number[];
  folderIds?: number[];
  operationType?: "MOVE" | "COPY" | "CLONE";
}

export interface MoveExplorerItemsResponse {
  data: {
    operationType: "MOVE" | "COPY" | "CLONE";
    destinationFolderId: number | null;
    destinationFolderName: string;
    requested: {
      folders: number;
      testCases: number;
    };
    moved: {
      folders: number;
      testCases: number;
    };
    skipped: {
      testCasesInsideMovedFolders: number;
    };
    successCount: number;
    failureCount: number;
  };
}

export const moveExplorerItems = (payload: MoveExplorerItemsPayload) => {
  const endpoint = API_ENDPOINTS.TESTCASE.moveItems;
  return httpClient<MoveExplorerItemsResponse>(
    httpMethods.POST,
    endpoint,
    payload,
  );
};

export const fetchFolderList = (
  versionId?: number,
  type?: string,
  countType?: string,
) => {
  let query = versionId ? `?workspaceVersionId=${versionId}` : "";
  if (type && type !== "all") query += `${query ? "&" : "?"}type=${type}`;
  if (countType) query += `${query ? "&" : "?"}countType=${countType}`;
  query += `${query ? "&" : "?"}_=${Date.now()}`;
  const endpoint = `${API_ENDPOINTS.TESTCASE.testCaseFolder}${query}`;
  return httpClient<Paginated<TestCaseFolder>>(httpMethods.GET, endpoint);
};

export const fetchTestCaseStats = (versionId?: number) => {
  const query = versionId ? `?workspaceVersionId=${versionId}` : "";
  return httpClient<{ total: number; automated: number; manual: number; updatedRecently: number }>(
    httpMethods.GET,
    `${API_ENDPOINTS.TESTCASE.testCaseStats}${query}`,
  );
};

export const fetchSubFolderList = (parentFolderId: number) => {
  const endpoint = `${API_ENDPOINTS.TESTCASE.testCaseFolder}/${parentFolderId}?_=${Date.now()}`;
  return httpClient(httpMethods.GET, endpoint);
};

export const fetchFolderDetails = (folderId: number) => {
  const endpoint = `${API_ENDPOINTS.TESTCASE.testCaseFolder}/details/${folderId}`;
  return httpClient<TestCaseFolder>(httpMethods.GET, endpoint);
};

export const fetchFolderStats = (folderId: number) => {
  const endpoint = `${API_ENDPOINTS.TESTCASE.testCaseFolder}/${folderId}/stats`;
  return httpClient<{
    total: number;
    automation: number;
    manual: number;
    health: "healthy" | "warning" | "critical";
    coverage: { requirements: number; automation: number };
    execution: { cycles: number; pass: number; fail: number; blocked: number };
  }>(httpMethods.GET, endpoint);
};

// ACTIVITY

export const fetchTestCaseComments = (
  versionId: string | number,
  testCaseId: string | number,
) => {
  const endpoint = `${API_ENDPOINTS.TESTCASE.testCaseComments}?query=workspaceVersionId:${versionId},moduleId:${testCaseId},moduleName:TestCase&sort=commentDate,desc`;
  return httpClient<Paginated<Comment>>(httpMethods.GET, endpoint);
};

export const createTestCaseComments = (payload: Partial<Comment>) => {
  const endpoint = `${API_ENDPOINTS.TESTCASE.testCaseComments}`;
  return httpClient<void>(httpMethods.POST, endpoint, payload);
};
export const fetchComments = (
  versionId: string,
  moduleId: number,
  moduleName: string,
  page: number = 0,
  size: number = 25,
) => {
  const endpoint = `${API_ENDPOINTS.COMMENT.comments}?query=workspaceVersionId:${versionId},moduleId:${moduleId},moduleName:${moduleName}&sort=commentDate,asc&page=${page}&size=${size}`;
  return httpClient<Paginated<Comment>>(httpMethods.GET, endpoint);
};

/**
 * Fetches comments for a Defect by moduleId only — does NOT filter by workspaceVersionId
 * so that all team members see the same comments regardless of their version preference.
 */
export const fetchDefectComments = (
  moduleId: number,
  page: number = 0,
  size: number = 25,
) => {
  const endpoint = `${API_ENDPOINTS.COMMENT.comments}?query=moduleId:${moduleId},moduleName:Defect&sort=commentDate,asc&page=${page}&size=${size}`;
  return httpClient<Paginated<Comment>>(httpMethods.GET, endpoint);
};
export const fetchHistory = (
  versionId: string,
  moduleId: number,
  moduleName: string,
  page: number = 0,
  size: number = 25,
) => {
  const endpoint = `${API_ENDPOINTS.HISTORY.history}?query=workspaceVersionId:${versionId},moduleId:${moduleId},moduleName:${moduleName}&sort=eventDate,desc&page=${page}&size=${size}`;
  return httpClient<Paginated<History>>(httpMethods.GET, endpoint);
};
export const updateTestCaseComments = (
  commentId: number,
  payload: Partial<Comment>,
) => {
  const endpoint = `${API_ENDPOINTS.TESTCASE.testCaseComments}/${commentId}`;
  return httpClient<void>(httpMethods.PUT, endpoint, payload);
};

export const deleteTestCaseComments = (commentId: number) => {
  const endpoint = `${API_ENDPOINTS.TESTCASE.testCaseComments}/${commentId}`;
  return httpClient<void>(httpMethods.DELETE, endpoint);
};

export const togglePinComment = (commentId: number) => {
  const endpoint = `${API_ENDPOINTS.TESTCASE.testCaseComments}/${commentId}/pin`;
  return httpClient<void>(httpMethods.PUT, endpoint);
};

// Test Steps
export const createTestSteps = (payload: Partial<TestSteps>) => {
  const endpoint = `${API_ENDPOINTS.RECORDER.testSteps}`;
  return httpClient<TestSteps>(httpMethods.POST, endpoint, payload);
};

/**
 * Per-step result for a batch create, index-aligned with the request array so
 * the caller can attach ids to the steps that saved and roll back ONLY the ones
 * that failed (never re-creating — and duplicating — the ones that did save).
 */
export interface BatchStepResult {
  ok: boolean;
  step?: TestSteps;
  error?: string;
}

export interface CreateTestStepsBatchResponse {
  results: BatchStepResult[];
  created: TestSteps[];
}

/**
 * Creates many steps in ONE request. The server loops sequentially (so element
 * upserts stay race-free) and isolates each step, so a single bad step can't
 * abort the whole batch. Used by "Add all" so the Steps list fills in one shot
 * instead of one-by-one. Uses a longer timeout because it writes N rows against
 * a high-latency remote DB (mirrors createBulkFolder).
 */
export const createTestStepsBatch = (steps: Partial<TestSteps>[]) => {
  const endpoint = `${API_ENDPOINTS.RECORDER.testSteps}/batch`;
  return httpClient<CreateTestStepsBatchResponse>(
    httpMethods.POST,
    endpoint,
    { steps },
    { timeout: 120_000 },
  );
};

export const updateTestSteps = (
  testStepId: number,
  payload: Partial<TestSteps>,
) => {
  const endpoint = `${API_ENDPOINTS.RECORDER.testSteps}/${testStepId}`;
  return httpClient<void>(httpMethods.PUT, endpoint, payload);
};

export const fetchRestResponse = (payload: Record<string, unknown>) => {
  const endpoint = `${API_ENDPOINTS.TESTCASE.fetchRestResponse}`;
  return httpClient(httpMethods.POST, endpoint, payload);
};

// Delete Test Case
export const deletTestCaseById = (testCaseId: number) => {
  const endpoint = `${API_ENDPOINTS.TESTCASE.testCase}${testCaseId}/mark_as_delete`;
  return httpClient<void>(httpMethods.DELETE, endpoint);
};

// Fetch test cases that use the given test case as a prerequisite
export const fetchTestCasesUsingAsPrerequisite = (testCaseId: number) => {
  const endpoint = `${API_ENDPOINTS.TESTCASE.testCase}pre_requisite/${testCaseId}`;
  return httpClient<Paginated<TestCases>>(httpMethods.GET, endpoint);
};

export type TestCasePlanRef = { id: number; name: string | null };
export type TestCasePlansMap = Record<string, TestCasePlanRef[]>;

// Bulk: get the test plans each given test case belongs to.
export const fetchTestPlansForTestCases = (
  testCaseIds: (number | string)[],
) => {
  const endpoint = API_ENDPOINTS.TESTCASE.testCasePlans;
  return httpClient<TestCasePlansMap>(httpMethods.POST, endpoint, {
    testCaseIds,
  });
};

// Delete Test Steps
export const deleteTestStepById = (testStepId: number) => {
  const endpoint = `${API_ENDPOINTS.TESTCASE.testSteps}/${testStepId}`;
  return httpClient<void>(httpMethods.DELETE, endpoint);
};

// Bulk Update Test Steps (for reordering - sends full step objects in request body)
export const bulkUpdateSteps = (steps: any[]) => {
  const endpoint = `${API_ENDPOINTS.RECORDER.bulkUpdateSteps}`;
  // Send array of step objects in request body (not query params)
  return httpClient<void>(httpMethods.PUT, endpoint, steps);
};

// Bulk Update Test Steps Properties (for property updates via query params)
export const bulkUpdateTestSteps = (params: Record<string, any>) => {
  const queryString = buildQueryString(params);
  const endpoint = `${API_ENDPOINTS.RECORDER.bulkUpdateStepsProps}?${queryString}`;
  return httpClient<void>(httpMethods.PUT, endpoint);
};
// Bulk Delete Test Steps
export const bulkDeleteTestSteps = (params: Record<string, any>) => {
  const queryString = buildQueryString(params);
  const endpoint = `${API_ENDPOINTS.RECORDER.bulkDeleteSteps}?${queryString}`;
  return httpClient<void>(httpMethods.DELETE, endpoint);
};
export const createTestCasetype = (payload: Partial<TestCaseType>) => {
  const endpoint = `${API_ENDPOINTS.TESTCASE.testCaseTypes}`;
  return httpClient<void>(httpMethods.POST, endpoint, payload);
};

export const deleteTestCasetypebyId = (testcasetypeid: number) => {
  const endpoint = `${API_ENDPOINTS.TESTCASE.testCaseTypes}${testcasetypeid}`;
  return httpClient<void>(httpMethods.DELETE, endpoint);
};

export const updateTestCaseTypesById = (
  typeId: number,
  payload: Partial<TestCaseType>,
) => {
  const endpoint = `${API_ENDPOINTS.TESTCASE.testCaseTypes}${typeId}`;
  return httpClient<void>(httpMethods.PUT, endpoint, payload);
};

export const bulkUpdateManualTestSteps = (
  testStepRequests: Partial<TestSteps>[],
) => {
  const endpoint = `${API_ENDPOINTS.TESTSTEP.bulkUpdateTestSteps}`; // define this in your API_ENDPOINTS
  return httpClient<void>(httpMethods.PUT, endpoint, testStepRequests);
};

// Test Case Import / Export
export const exportTestCasesExcel = (versionId: number) => {
  return httpClient<string>(
    httpMethods.POST,
    `${API_ENDPOINTS.TESTCASE.testCase}exportexcel`,
    versionId,
  );
};

export const exportSelectedTestCasesExcel = (ids: number[]) => {
  return httpClient<string>(
    httpMethods.POST,
    `${API_ENDPOINTS.TESTCASE.testCase}exportexcel/selected`,
    ids,
  );
};

export const importTestCasesExcel = (file: File, folderId?: number) => {
  const formData = new FormData();
  formData.append("file", file);
  const url = folderId
    ? `${API_ENDPOINTS.TESTCASE.testCase}upload?folderId=${folderId}`
    : `${API_ENDPOINTS.TESTCASE.testCase}upload`;
  return httpClient<void>(httpMethods.MULTIPART, url, formData);
};

export const exportStepGroupsXml = (options: Record<string, any>) => {
  return httpClient<void>(httpMethods.POST, API_ENDPOINTS.IMPORTEXPORT.export, {
    isTestCaseEnabled: false,
    isTestStepEnabled: false,
    isRestStepEnabled: false,
    isTestDataEnabled: false,
    isElementEnabled: false,
    isElementScreenNameEnabled: false,
    isUploadsEnabled: false,
    isTestCasePriorityEnabled: false,
    isTestCaseTypeEnabled: false,
    isSuitesEnabled: false,
    isTestPlanEnabled: false,
    isAttachmentEnabled: false,
    isAgentEnabled: false,
    isLabelEnabled: false,
    ...options,
  });
};

// ── Data Generators ──────────────────────────────────────────────────────────

export const fetchDataGenerators = (search?: string, page = 0, size = 50) => {
  let query = `page=${page}&size=${size}`;
  if (search) query += `&query=term:*${encodeURIComponent(search)}*`;
  const endpoint = `${API_ENDPOINTS.TESTCASE.defaultDataGenerators}?${query}`;
  return httpClient(httpMethods.GET, endpoint);
};

export const fetchDataGeneratorById = (id: number) => {
  const endpoint = `${API_ENDPOINTS.TESTCASE.defaultDataGenerators}/${id}`;
  return httpClient(httpMethods.GET, endpoint);
};

// ── Runtime Variables ─────────────────────────────────────────────────────────

export const fetchStoreActionNlpTemplates = () => {
  const endpoint = `${API_ENDPOINTS.TESTCASE.naturalTextActions}?query=action:store&page=0&size=200`;
  return httpClient<Paginated<NaturalTextActions>>(httpMethods.GET, endpoint);
};

export const fetchStepsForNlpTemplateIds = (
  templateIds: number[],
  versionId: number,
  workspaceId: number,
) => {
  const idsParam = encodeURIComponent(templateIds.join("#"));
  const endpoint = `${API_ENDPOINTS.TESTCASE.testSteps}?query=naturalTextActionId@${idsParam},workspaceVersionId:${versionId},workspaceId:${workspaceId}&sort=position&page=0&size=100`;
  return httpClient<Paginated<TestSteps>>(httpMethods.GET, endpoint);
};

export const fetchTestCaseById2 = (testCaseId: number) => {
  const endpoint = `${API_ENDPOINTS.TESTCASE.testCase}${testCaseId}`;
  return httpClient<TestCases>(httpMethods.GET, endpoint);
};

// ── Manual Test Runs ──────────────────────────────────────────────────────────

export const fetchManualTestRuns = (testCaseId: number | string) =>
  httpClient<Paginated<ManualTestRun>>(
    httpMethods.GET,
    `manual_test_runs?testCaseId=${testCaseId}`,
  );

export const fetchManualTestRun = (runId: number | string) =>
  httpClient<ManualTestRun>(httpMethods.GET, `manual_test_runs/${runId}`);

export const createManualTestRun = (testCaseId: number | string) =>
  httpClient<ManualTestRun>(httpMethods.POST, "manual_test_runs", {
    testCaseId,
  });

export const updateManualTestRunStep = (
  stepId: number | string,
  status: string,
  comment?: string,
  imageUrl?: string,
) =>
  httpClient<void>(httpMethods.PATCH, `manual_test_runs/steps/${stepId}`, {
    status,
    comment,
    imageUrl,
  });

export const completeManualTestRun = (runId: number | string, notes?: string) =>
  httpClient<void>(httpMethods.PATCH, `manual_test_runs/${runId}/complete`, {
    notes,
  });

export const deleteManualTestRun = (runId: number | string) =>
  httpClient<void>(httpMethods.DELETE, `manual_test_runs/${runId}`);

export const uploadManualRunImage = (file: File) => {
  const form = new FormData();
  form.append("image", file);
  return httpClient(httpMethods.MULTIPART, "manual_test_runs/upload", form);
};
